import i18n from '@/i18n';
/**
 * 视频空间元数据与列表展示辅助。
 * 含：收藏/回收站/内容 Tab 过滤、列表行归一化、文件夹本地存储、工作区归属、采集 source 判定等。
 */
import { normalizeWorkspaceMode } from './accountCenterUtils';

import { userLocal } from '@/utils/ipeUserLocalState';

const FOLDERS_STORAGE_KEY = 'ipe-video-space-folders-v1';
const FOLDERS_TEAM_KEY_PREFIX = `${FOLDERS_STORAGE_KEY}-team-`;

export const VIDEO_SPACE_TRASH_RETENTION_DAYS = 7;
export const VIDEO_SPACE_TRASH_RETENTION_MS = VIDEO_SPACE_TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;

export const VIDEO_SPACE_CONTENT_TABS = [
  { id: 'favorites', label: '我的收藏' },
  { id: 'trash', label: '回收站' },
];

/**
 * 将工作台顶栏 nav id 映射为视频空间内部内容 Tab（收藏/回收站/默认全部）。
 *
 * @param {string} navId 顶栏 nav id
 * @returns {'favorites' | 'trash' | 'all'}
 */
export function resolveVideoSpaceContentTab(navId) {
  if (navId === 'spaceFavorites') return 'favorites';
  if (navId === 'spaceTrash') return 'trash';
  if (navId === 'spaceCollection') return 'all';
  return 'all';
}

/**
 * 文生视频任务入库视为「采集」分类，图生视频等为素材。
 *
 * @param {string} source 视频来源字段
 * @returns {boolean}
 */
export function isVideoSpaceCollectionSource(source) {
  return String(source || '').trim().toLowerCase() === 'texttovideo';
}

function normalizeFolderId(value) {
  return value == null || value === '' ? '' : String(value).trim();
}

function normalizeFolder(item) {
  if (!item || !item.id) return null;
  const name = String(item.name || '').trim();
  if (!name) return null;
  return {
    id: normalizeFolderId(item.id),
    name,
    parentId: normalizeFolderId(item.parentId),
    scope: normalizeVideoSpaceFolderScope(item.scope),
    teamShared: item.teamShared === true || String(item.teamShared) === 'true',
    ownerId: item.ownerId != null && item.ownerId !== '' ? String(item.ownerId) : '',
    ownerUserId: item.ownerUserId != null && item.ownerUserId !== '' ? String(item.ownerUserId) : '',
    teamId: item.teamId != null && item.teamId !== '' ? String(item.teamId) : '',
    createdAt: item.createdAt || new Date().toISOString(),
    deletedAt: item.deletedAt || null,
  };
}

function groupActiveFoldersByParent(folders) {
  const grouped = new Map();
  (Array.isArray(folders) ? folders : [])
    .filter((item) => item && !isVideoSpaceFolderDeleted(item))
    .forEach((item) => {
      const parentId = normalizeFolderId(item.parentId);
      const bucket = grouped.get(parentId) || [];
      bucket.push(item);
      grouped.set(parentId, bucket);
    });
  return grouped;
}

/**
 * 文件夹是否处于回收站（软删除）状态。
 *
 * @param {object|null|undefined} folder 文件夹对象
 * @returns {boolean}
 */
export function isVideoSpaceFolderDeleted(folder) {
  return Boolean(folder?.deletedAt);
}

/**
 * 计算软删除项应被永久清理的时间戳（删除时刻 + 保留天数）。
 *
 * @param {string|number|Date} deletedAt 删除时间
 * @returns {number} 毫秒时间戳，非法 deletedAt 时为 0
 */
export function getTrashPurgeTime(deletedAt) {
  const timestamp = new Date(deletedAt).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return timestamp + VIDEO_SPACE_TRASH_RETENTION_MS;
}

/**
 * 回收站项是否已超过保留期（应永久删除）。
 *
 * @param {string|number|Date|null|undefined} deletedAt 删除时间
 * @returns {boolean}
 */
export function isTrashExpired(deletedAt) {
  if (!deletedAt) return false;
  return Date.now() >= getTrashPurgeTime(deletedAt);
}

/**
 * 回收站项距离永久删除的剩余整天数。
 *
 * @param {string|number|Date|null|undefined} deletedAt 删除时间
 * @returns {number} 剩余天数，已过期为 0
 */
export function getTrashRemainingDays(deletedAt) {
  if (!deletedAt) return VIDEO_SPACE_TRASH_RETENTION_DAYS;
  const remaining = getTrashPurgeTime(deletedAt) - Date.now();
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / (24 * 60 * 60 * 1000));
}

/**
 * 回收站卡片展示的剩余保留期文案。
 *
 * @param {string|number|Date|null|undefined} deletedAt 删除时间
 * @returns {string}
 */
export function formatTrashRemainingLabel(deletedAt) {
  const days = getTrashRemainingDays(deletedAt);
  if (days <= 0) return i18n.t('workbench:spaceBoard.trashPermanentSoon');
  return i18n.t('workbench:spaceBoard.trashDaysLeft', { days });
}

/**
 * 按回收站剩余天数升序排序（同天数按 deletedAt 早晚）。
 *
 * @param {object[]} items 含 deletedAt 的列表项
 * @returns {object[]} 新数组
 */
export function sortByTrashRemainingDays(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const daysA = getTrashRemainingDays(a.deletedAt);
    const daysB = getTrashRemainingDays(b.deletedAt);
    if (daysA !== daysB) return daysA - daysB;
    const timeA = new Date(a.deletedAt || 0).getTime();
    const timeB = new Date(b.deletedAt || 0).getTime();
    return timeA - timeB;
  });
}

export function isVideoSpaceFolderShared(folder) {
  return folder?.teamShared === true || String(folder?.teamShared) === 'true';
}

export function canReadVideoSpaceFolder(folder, {
  authUserId = '',
  permissionScope = 'self',
} = {}) {
  if (!folder) return false;
  if (permissionScope === 'team_all') return true;
  const userId = authUserId != null && authUserId !== '' ? String(authUserId) : '';
  const ownerId = String(folder.ownerId || folder.ownerUserId || '');
  if (userId && ownerId && ownerId === userId) return true;
  return isVideoSpaceFolderShared(folder);
}

export function filterReadableVideoSpaceFolders(folders, access = {}) {
  return (Array.isArray(folders) ? folders : []).filter((folder) => canReadVideoSpaceFolder(folder, access));
}

function teamFoldersCacheKey(teamId) {
  return `${FOLDERS_STORAGE_KEY}-team-${String(teamId || 'unknown')}`;
}

export function loadTeamVideoSpaceFolderCache(teamId) {
  try {
    const parsed = userLocal.getJson(teamFoldersCacheKey(teamId), null);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeFolder).filter(Boolean);
  } catch {
    return [];
  }
}

/** 按当前工作区读取文件夹：团队走 team 缓存，个人走 localStorage（绝不混用） */
export function loadWorkspaceVideoSpaceFolders({ teamMode = false, teamId = '' } = {}) {
  if (teamMode) {
    return teamId ? loadTeamVideoSpaceFolderCache(teamId) : [];
  }
  return loadVideoSpaceFolders();
}

export function saveTeamVideoSpaceFolderCache(folders, teamId) {
  try {
    const normalized = (folders || []).map(normalizeFolder).filter(Boolean);
    userLocal.setJson(teamFoldersCacheKey(teamId), normalized);
    return normalized;
  } catch {
    return folders || [];
  }
}

export function loadVideoSpaceFolders() {
  try {
    const parsed = userLocal.getJson(FOLDERS_STORAGE_KEY, null);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeFolder).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveVideoSpaceFolders(folders) {
  try {
    const normalized = (folders || []).map(normalizeFolder).filter(Boolean);
    userLocal.setJson(FOLDERS_STORAGE_KEY, normalized);
    return normalized;
  } catch {
    return folders || [];
  }
}

export function clearVideoSpaceFoldersForLogout() {
  userLocal.dropLegacyKeys(FOLDERS_STORAGE_KEY);
  userLocal.dropLegacyKeyPrefix(FOLDERS_TEAM_KEY_PREFIX);
}

export function filterVideoSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  const normalizedParentId = normalizeFolderId(parentId);
  return list.filter((item) => normalizeFolderId(item.parentId) === normalizedParentId);
}

export function filterActiveVideoSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  return filterVideoSpaceFoldersByParent(
    list.filter((item) => !isVideoSpaceFolderDeleted(item)),
    parentId,
  );
}

export function filterTrashedVideoSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  const normalizedParentId = parentId ? String(parentId) : '';

  return list.filter((item) => {
    if (!isVideoSpaceFolderDeleted(item)) return false;
    if (String(item.parentId || '') === normalizedParentId) return true;
    if (!normalizedParentId && item.parentId) {
      const parent = list.find((folder) => folder.id === item.parentId);
      return Boolean(parent && !isVideoSpaceFolderDeleted(parent));
    }
    return false;
  });
}

export function buildVideoSpaceFolderPath(folders, folderId) {
  if (!folderId) return [];
  const list = Array.isArray(folders) ? folders : [];
  const map = new Map(list.map((item) => [item.id, item]));
  const path = [];
  let current = map.get(String(folderId));
  const guard = new Set();

  while (current && !guard.has(current.id)) {
    guard.add(current.id);
    path.unshift(current);
    current = current.parentId ? map.get(String(current.parentId)) : null;
  }

  return path;
}

export function collectVideoSpaceFolderDescendantIds(folders, folderId) {
  const list = Array.isArray(folders) ? folders : [];
  const rootId = normalizeFolderId(folderId);
  if (!rootId) return new Set();

  const ids = new Set([rootId]);
  let changed = true;

  while (changed) {
    changed = false;
    // 改用 for...of，完全消除了循环内的函数声明
    for (const item of list) {
      const itemId = normalizeFolderId(item.id);
      const parentId = normalizeFolderId(item.parentId);
      if (parentId && ids.has(parentId) && !ids.has(itemId)) {
        ids.add(itemId);
        changed = true;
      }
    }
  }

  return ids;
}

function createFolderId(seed = Date.now()) {
  return `folder-${seed}-${Math.random().toString(36).slice(2, 8)}`;
}

const VIDEO_SPACE_FOLDER_NAME_MAX_LEN = 40;
export const VIDEO_SPACE_FOLDER_DUPLICATE_NAME_MESSAGE = '同级已有同名文件夹，请使用其他名称';
export const VIDEO_SPACE_FOLDER_DEFAULT_SCOPE = 'material';

export function normalizeVideoSpaceFolderScope(scope) {
  return scope === 'collection' ? 'collection' : VIDEO_SPACE_FOLDER_DEFAULT_SCOPE;
}

export function resolveVideoSpaceFolderScope(folder) {
  return normalizeVideoSpaceFolderScope(folder?.scope);
}

export function resolveVideoSpaceFolderScopeForVideo(video) {
  return isVideoSpaceCollectionSource(video?.source) ? 'collection' : VIDEO_SPACE_FOLDER_DEFAULT_SCOPE;
}

export function resolveVideoSpaceFolderCreateScope(folders, parentId, preferredScope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE) {
  const normalizedParentId = normalizeFolderId(parentId);
  if (normalizedParentId) {
    const parent = (Array.isArray(folders) ? folders : []).find(
      (item) => normalizeFolderId(item.id) === normalizedParentId,
    );
    if (parent) return resolveVideoSpaceFolderScope(parent);
  }
  return normalizeVideoSpaceFolderScope(preferredScope);
}

export function filterFoldersForSourceCategory(folders, parentId, category) {
  const list = Array.isArray(folders) ? folders : [];
  const children = filterVideoSpaceFoldersByParent(
    list.filter((item) => !isVideoSpaceFolderDeleted(item)),
    parentId,
  );
  if (category === 'all') return children;
  const scope = category === 'collection' ? 'collection' : VIDEO_SPACE_FOLDER_DEFAULT_SCOPE;
  return children.filter((item) => resolveVideoSpaceFolderScope(item) === scope);
}

export function isVideoSpaceFolderNameTaken(
  folders,
  name,
  parentId = '',
  excludeFolderId = '',
  scope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE,
) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return false;
  const normalizedParentId = normalizeFolderId(parentId);
  const excludeId = normalizeFolderId(excludeFolderId);
  const normalizedScope = normalizeVideoSpaceFolderScope(scope);
  return (Array.isArray(folders) ? folders : []).some((item) => {
    if (!item || isVideoSpaceFolderDeleted(item)) return false;
    if (excludeId && normalizeFolderId(item.id) === excludeId) return false;
    if (normalizeFolderId(item.parentId) !== normalizedParentId) return false;
    if (resolveVideoSpaceFolderScope(item) !== normalizedScope) return false;
    return String(item.name || '').trim() === trimmed;
  });
}

function buildUniqueVideoSpaceFolderName(
  folders,
  desiredName,
  parentId = '',
  excludeFolderId = '',
  scope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE,
) {
  const trimmed = String(desiredName || '').trim();
  if (!trimmed) return trimmed;
  if (!isVideoSpaceFolderNameTaken(folders, trimmed, parentId, excludeFolderId, scope)) {
    return trimmed;
  }

  let counter = 2;
  while (counter < 10000) {
    const suffix = ` (${counter})`;
    const baseMaxLen = VIDEO_SPACE_FOLDER_NAME_MAX_LEN - suffix.length;
    const base = trimmed.length > baseMaxLen ? trimmed.slice(0, baseMaxLen) : trimmed;
    const candidate = `${base}${suffix}`;
    if (!isVideoSpaceFolderNameTaken(folders, candidate, parentId, excludeFolderId, scope)) {
      return candidate;
    }
    counter += 1;
  }

  return `${trimmed.slice(0, VIDEO_SPACE_FOLDER_NAME_MAX_LEN - 4)}_${Date.now() % 10000}`;
}

/** 构建新文件夹对象（不写入任何存储，供团队/个人各自持久化） */
export function buildNewVideoSpaceFolder(
  name,
  parentId = '',
  preferredScope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE,
  folders = null,
) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;
  const list = Array.isArray(folders) ? folders : loadVideoSpaceFolders();
  const normalizedParentId = normalizeFolderId(parentId);
  const scope = resolveVideoSpaceFolderCreateScope(list, normalizedParentId, preferredScope);
  if (isVideoSpaceFolderNameTaken(list, trimmed, normalizedParentId, '', scope)) {
    return null;
  }
  return {
    id: createFolderId(),
    name: trimmed,
    parentId: normalizedParentId,
    scope,
    createdAt: new Date().toISOString(),
  };
}

export function createVideoSpaceFolder(name, parentId = '', preferredScope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE) {
  const folders = loadVideoSpaceFolders();
  const folder = buildNewVideoSpaceFolder(name, parentId, preferredScope, folders);
  if (!folder) return null;
  saveVideoSpaceFolders([...folders, folder]);
  return folder;
}

export function renameVideoSpaceFolder(folderId, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;
  const folders = loadVideoSpaceFolders();
  const targetId = String(folderId || '');
  const current = folders.find((item) => item.id === targetId);
  if (!current) return null;
  if (isVideoSpaceFolderNameTaken(
    folders,
    trimmed,
    current.parentId,
    targetId,
    resolveVideoSpaceFolderScope(current),
  )) {
    return null;
  }
  const next = folders.map((item) => (
    item.id === targetId ? { ...item, name: trimmed } : item
  ));
  saveVideoSpaceFolders(next);
  return next.find((item) => item.id === targetId) || null;
}

export function buildVideoSpaceFolderCopyName(folderName, folders = null, parentId = '', scope = VIDEO_SPACE_FOLDER_DEFAULT_SCOPE) {
  const base = String(folderName || '').trim() || i18n.t('workbench:spaceBoard.unnamedFolder');
  const copySuffix = i18n.t('workbench:spaceBoard.copyNameSuffix');
  if (!folders) return `${base}${copySuffix}`;

  const normalizedParentId = normalizeFolderId(parentId);
  const normalizedScope = normalizeVideoSpaceFolderScope(scope);
  let counter = 1;
  while (counter < 10000) {
    const suffix = counter === 1 ? copySuffix : `${copySuffix}${counter}`;
    const baseMaxLen = VIDEO_SPACE_FOLDER_NAME_MAX_LEN - suffix.length;
    const truncatedBase = base.length > baseMaxLen ? base.slice(0, baseMaxLen) : base;
    const candidate = `${truncatedBase}${suffix}`;
    if (!isVideoSpaceFolderNameTaken(folders, candidate, normalizedParentId, '', normalizedScope)) {
      return candidate;
    }
    counter += 1;
  }

  return buildUniqueVideoSpaceFolderName(folders, `${base}${copySuffix}`, normalizedParentId, '', normalizedScope);
}

export function copyVideoSpaceFolder(folderId, copyName) {
  const folders = loadVideoSpaceFolders();
  const sourceId = String(folderId || '');
  const source = folders.find((item) => item.id === sourceId);
  if (!source) return null;

  const rootName = String(copyName || '').trim() || buildVideoSpaceFolderCopyName(
    source.name,
    folders,
    source.parentId,
    resolveVideoSpaceFolderScope(source),
  );
  if (isVideoSpaceFolderNameTaken(
    folders,
    rootName,
    source.parentId,
    '',
    resolveVideoSpaceFolderScope(source),
  )) {
    return null;
  }
  const subtreeIds = collectVideoSpaceFolderDescendantIds(folders, sourceId);
  const subtree = folders.filter((item) => subtreeIds.has(item.id));
  const idMap = new Map();
  let seed = Date.now();

  subtree.forEach((item) => {
    idMap.set(item.id, createFolderId(seed));
    seed += 1;
  });

  const copied = subtree
    .filter((item) => !isVideoSpaceFolderDeleted(item))
    .map((item) => ({
      ...item,
      id: idMap.get(item.id),
      parentId: item.id === sourceId
        ? String(source.parentId || '')
        : String(idMap.get(item.parentId) || ''),
      name: item.id === sourceId ? rootName : item.name,
      createdAt: new Date().toISOString(),
      deletedAt: null,
    }));

  saveVideoSpaceFolders([...folders, ...copied]);
  return copied.find((item) => item.id === idMap.get(sourceId)) || copied[0] || null;
}

export function moveVideoSpaceFolder(folderId, newParentId = '') {
  const folders = loadVideoSpaceFolders();
  const targetId = String(folderId || '');
  const nextParentId = newParentId ? String(newParentId) : '';
  const descendants = collectVideoSpaceFolderDescendantIds(folders, targetId);

  const movingFolder = folders.find((item) => item.id === targetId);
  if (!movingFolder) return false;
  if (nextParentId && descendants.has(nextParentId)) return false;
  const movingScope = resolveVideoSpaceFolderScope(movingFolder);
  if (nextParentId) {
    const newParent = folders.find((item) => item.id === nextParentId);
    if (!newParent || resolveVideoSpaceFolderScope(newParent) !== movingScope) return false;
  }
  if (isVideoSpaceFolderNameTaken(
    folders,
    movingFolder.name,
    nextParentId,
    targetId,
    movingScope,
  )) {
    return false;
  }

  const next = folders.map((item) => (
    item.id === targetId ? { ...item, parentId: nextParentId } : item
  ));
  saveVideoSpaceFolders(next);
  return true;
}

export function removeVideoSpaceFolders(folderIds) {
  const removeSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  const folders = loadVideoSpaceFolders();
  const next = folders.filter((item) => !removeSet.has(item.id));
  saveVideoSpaceFolders(next);
  return next;
}

export function trashVideoSpaceFolders(folderIds, deletedAt = new Date().toISOString()) {
  const trashSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  if (!trashSet.size) return loadVideoSpaceFolders();
  const folders = loadVideoSpaceFolders();
  const next = folders.map((item) => (
    trashSet.has(item.id) ? { ...item, deletedAt } : item
  ));
  saveVideoSpaceFolders(next);
  return next;
}

export function restoreVideoSpaceFolders(folderIds) {
  const restoreSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  if (!restoreSet.size) return loadVideoSpaceFolders();
  const folders = loadVideoSpaceFolders();
  const next = folders.map((item) => (
    restoreSet.has(item.id) ? { ...item, deletedAt: null } : item
  ));
  saveVideoSpaceFolders(next);
  return next;
}

/**
 * 从本地文件夹缓存中移除已超过保留期的回收站文件夹。
 *
 * @returns {string[]} 被永久移除的文件夹 id 列表
 */
export function purgeExpiredTrashedFolders() {
  const folders = loadVideoSpaceFolders();
  const removedIds = folders
    .filter((item) => isVideoSpaceFolderDeleted(item) && isTrashExpired(item.deletedAt))
    .map((item) => item.id);
  if (!removedIds.length) return [];
  removeVideoSpaceFolders(removedIds);
  return removedIds;
}

export function buildVideoSpaceFolderMoveTree(folders, folderId, { scope } = {}) {
  let list = (Array.isArray(folders) ? folders : [])
    .map(normalizeFolder)
    .filter(Boolean);
  if (scope) {
    const normalizedScope = normalizeVideoSpaceFolderScope(scope);
    list = list.filter((item) => resolveVideoSpaceFolderScope(item) === normalizedScope);
  }
  const excludeIds = collectVideoSpaceFolderDescendantIds(list, folderId);
  const childrenByParent = groupActiveFoldersByParent(list);

  const buildNodes = (parentId, depth) => {
    const normalizedParentId = normalizeFolderId(parentId);
    return (childrenByParent.get(normalizedParentId) || [])
      .filter((item) => !excludeIds.has(normalizeFolderId(item.id)))
      .map((item) => ({
        id: item.id,
        name: item.name,
        depth,
        children: buildNodes(item.id, depth + 1),
      }));
  };

  return {
    id: '',
    name: '全部视频',
    depth: 0,
    children: buildNodes('', 1),
  };
}

/** @deprecated 使用 buildVideoSpaceFolderMoveTree */
export function buildVideoSpaceFolderMoveOptions(folders, folderId) {
  const tree = buildVideoSpaceFolderMoveTree(folders, folderId);
  const flatten = (node) => [
    { id: node.id, name: node.name, depth: node.depth },
    ...(node.children || []).flatMap(flatten),
  ];
  return flatten(tree);
}

export function collectVideoIdsInFolders(videos, folderIds) {
  const idSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  return (Array.isArray(videos) ? videos : [])
    .filter((item) => idSet.has(String(item.folderId || '')))
    .map((item) => item.id);
}

export function collectVideoSpaceFolderSubtreeIds(folders, folderIds) {
  const roots = Array.isArray(folderIds) ? folderIds : [...folderIds];
  const allIds = new Set();
  roots.forEach((folderId) => {
    collectVideoSpaceFolderDescendantIds(folders, folderId).forEach((id) => allIds.add(id));
  });
  return allIds;
}

/**
 * 将选中文件夹（含子级）与视频合并为一次移入回收站的清单（仅未删除项）。
 *
 * @param {object[]} folders 文件夹列表
 * @param {object[]} videos 视频列表
 * @param {{ folderIds?: string[], videoIds?: string[] }} [options] 选中的根文件夹与直接选中的视频 id
 * @returns {{ folderCount: number, imageCount: number, folderIds: string[], videoIds: string[] }}
 */
export function summarizeActiveFolderTrash(folders, videos, { folderIds = [], videoIds = [] } = {}) {
  const folderRootIds = Array.isArray(folderIds) ? folderIds : [];
  const directVideoIds = Array.isArray(videoIds) ? videoIds.filter(Boolean) : [];
  const subtreeIds = collectVideoSpaceFolderSubtreeIds(folders, folderRootIds);
  const nestedVideoIds = collectVideoIdsInFolders(
    (Array.isArray(videos) ? videos : []).filter((item) => !isVideoSpaceDeleted(item)),
    subtreeIds,
  );
  const mergedVideoIds = [...new Set([...directVideoIds, ...nestedVideoIds])];
  return {
    folderCount: subtreeIds.size,
    imageCount: mergedVideoIds.length,
    folderIds: [...subtreeIds],
    videoIds: mergedVideoIds,
  };
}

/**
 * 统计回收站内选中文件夹/视频永久删除时的影响范围（含子文件夹内已删视频）。
 *
 * @param {object[]} folders 文件夹列表
 * @param {object[]} videos 视频列表
 * @param {{ folderIds?: string[], videoIds?: string[] }} [options]
 * @returns {{ folderCount: number, imageCount: number, folderIds: string[], videoIds: string[] }}
 */
export function summarizeTrashPermanentDelete(folders, videos, { folderIds = [], videoIds = [] } = {}) {
  const folderRootIds = Array.isArray(folderIds) ? folderIds : [];
  const directVideoIds = Array.isArray(videoIds) ? videoIds : [];
  const subtreeIds = collectVideoSpaceFolderSubtreeIds(folders, folderRootIds);
  const nestedVideoIds = collectVideoIdsInFolders(
    (Array.isArray(videos) ? videos : []).filter((item) => isVideoSpaceDeleted(item)),
    subtreeIds,
  );
  const mergedVideoIds = [...new Set([...directVideoIds, ...nestedVideoIds])];
  return {
    folderCount: subtreeIds.size,
    imageCount: mergedVideoIds.length,
    folderIds: [...subtreeIds],
    videoIds: mergedVideoIds,
  };
}

/**
 * 删除确认弹窗展示文案（打开弹窗时预计算，避免大列表重复遍历）。
 *
 * @param {object} [params]
 * @param {string[]} [params.folderIds] 选中的文件夹 id
 * @param {string[]} [params.videoIds] 选中的视频 id
 * @param {object[]} [params.folders] 文件夹列表（用于取文件夹名）
 * @param {{ imageCount?: number }|null} [params.summary] 预计算的汇总
 * @returns {string}
 */
export function buildVideoSpaceDeleteTargetLabel({
  folderIds = [],
  videoIds = [],
  folders = [],
  summary = null,
} = {}) {
  const folderRootCount = folderIds.length;
  const directImageCount = videoIds.length;
  if (folderRootCount === 1 && directImageCount === 0) {
    const folder = (Array.isArray(folders) ? folders : []).find((item) => item.id === folderIds[0]);
    return i18n.t('workbench:spaceBoard.deleteFolderNamed', {
      name: folder?.name || i18n.t('workbench:spaceBoard.unnamedFolder'),
    });
  }
  if (folderRootCount > 0 && directImageCount > 0) {
    return i18n.t('workbench:spaceBoard.deleteFoldersAndVideos', {
      folders: folderRootCount,
      videos: directImageCount,
    });
  }
  if (folderRootCount > 0) {
    return folderRootCount > 1
      ? i18n.t('workbench:spaceBoard.deleteFolders', { count: folderRootCount })
      : i18n.t('workbench:spaceBoard.deleteThisFolder');
  }
  const effectiveCount = summary?.imageCount ?? directImageCount;
  if (effectiveCount > 1) {
    return i18n.t('workbench:spaceBoard.deleteVideos', { count: effectiveCount });
  }
  return i18n.t('workbench:spaceBoard.deleteThisVideo');
}

export function isVideoSpaceFavorite(video) {
  if (video != null && typeof video === 'object') {
    return resolveVideoSpaceFavoriteFlag(video.favorite) || resolveVideoSpaceFavoriteFlag(video.isFavorite);
  }
  return resolveVideoSpaceFavoriteFlag(video);
}

/**
 * 列表入库前统一 favorite / isFavorite 布尔字段。
 *
 * @param {Record<string, unknown>} video 原始列表行
 * @returns {Record<string, unknown>} 带规范化收藏标记的行
 */
export function normalizeVideoSpaceListRow(video) {
  if (!video || typeof video !== 'object') return video;
  const favorite = isVideoSpaceFavorite(video);
  return { ...video, favorite, isFavorite: favorite };
}

/**
 * 兼容 JSON/DB 中 boolean、字符串、数字形式的收藏标记。
 *
 * @param {*} value 原始收藏字段值
 * @returns {boolean}
 */
function resolveVideoSpaceFavoriteFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value == null || value === 0) return false;
  const text = String(value).trim().toLowerCase();
  return text === 'true' || text === '1' || text === 'yes';
}

/**
 * 视频是否处于回收站（软删除）状态。
 *
 * @param {object|null|undefined} video 视频记录
 * @returns {boolean}
 */
export function isVideoSpaceDeleted(video) {
  if (!video || typeof video !== 'object') return false;
  const deletedAt = video.deletedAt;
  if (deletedAt == null) return false;
  const text = String(deletedAt).trim();
  // 与后端 VideoSpaceDedupeService.isActive / 前端 isVideoSpaceListActiveRow 对齐
  if (!text || text === 'null' || text === 'undefined') return false;
  return true;
}

/**
 * 按素材/采集分类过滤活跃（未删除）视频。
 *
 * @param {object[]} videos 视频列表
 * @param {'all' | 'material' | 'collection'} category 分类
 * @returns {object[]}
 */
export function filterVideoSpaceBySourceCategory(videos, category) {
  const list = Array.isArray(videos) ? videos : [];
  const active = list.filter((item) => !isVideoSpaceDeleted(item));
  if (category === 'collection') {
    return active.filter((item) => isVideoSpaceCollectionSource(item.source));
  }
  if (category === 'material') {
    return active.filter((item) => !isVideoSpaceCollectionSource(item.source));
  }
  return active;
}

/**
 * 按内容 Tab（收藏/回收站/采集/默认活跃）过滤视频列表。
 *
 * @param {object[]} videos 视频列表
 * @param {string} tabId Tab 标识：favorites、trash、collection 等
 * @returns {object[]}
 */
export function filterVideoSpaceByContentTab(videos, tabId) {
  const list = Array.isArray(videos) ? videos : [];
  if (tabId === 'favorites') {
    return list.filter((item) => isVideoSpaceFavorite(item) && !isVideoSpaceDeleted(item));
  }
  if (tabId === 'trash') {
    return list.filter((item) => isVideoSpaceDeleted(item));
  }
  if (tabId === 'collection') {
    return list.filter((item) => !isVideoSpaceDeleted(item) && isVideoSpaceCollectionSource(item.source));
  }
  return list.filter((item) => !isVideoSpaceDeleted(item));
}

/**
 * 按 folderId 过滤视频（空 folderId 表示根目录）。
 *
 * @param {object[]} videos 视频列表
 * @param {string} folderId 文件夹 id
 * @returns {object[]}
 */
export function filterVideoSpaceByFolder(videos, folderId) {
  const list = Array.isArray(videos) ? videos : [];
  const normalizedFolderId = folderId ? String(folderId) : '';
  return list.filter((item) => String(item.folderId || '') === normalizedFolderId);
}

/**
 * 解析视频归属工作区。无标记的历史数据默认 personal，确保个人上传不会进入团队视图。
 *
 * @param {object|null|undefined} video 视频记录
 * @returns {'personal' | 'team'}
 */
export function resolveVideoRecordWorkspace(video) {
  const raw = String(video?.workspaceMode || video?.captureSpace || '').trim().toLowerCase();
  if (raw === 'team' || raw === 'personal') {
    return raw;
  }
  return 'personal';
}

/**
 * 按工作台个人/团队模式过滤视频（入库时写入 workspaceMode / captureSpace）。
 *
 * @param {object[]} videos 视频列表
 * @param {string} workspaceMode 当前工作区模式
 * @returns {object[]}
 */
export function filterVideoSpaceByWorkspaceMode(videos, workspaceMode) {
  const mode = normalizeWorkspaceMode(workspaceMode);
  return (Array.isArray(videos) ? videos : []).filter((video) => (
    resolveVideoRecordWorkspace(video) === mode
  ));
}

/**
 * 为视频对象写入工作区归属字段（上传/同步前调用）。
 *
 * @param {object} video 视频对象
 * @param {string} workspaceMode 工作区模式
 * @param {string|number|null} [teamId] 团队 id，团队模式下可选
 * @returns {object} 新对象
 */
export function stampVideoSpaceWorkspaceFields(video, workspaceMode, teamId = null) {
  if (!video || typeof video !== 'object') return video;
  const mode = normalizeWorkspaceMode(workspaceMode);
  const next = { ...video, workspaceMode: mode, captureSpace: mode };
  if (mode === 'team' && teamId != null && teamId !== '') {
    next.teamId = teamId;
  }
  return next;
}
